import React, { useMemo, useState } from 'react';
import { AiOrb } from '../components/AiOrb.js';
import { AvatarInitials } from '../components/AvatarInitials.js';
import { KpiCard } from '../components/KpiCard.js';
import { Waveform } from '../components/Waveform.js';
import aclLogoBlue from '../assets/acl-logo-blue.svg';
import {
  type DemoChannel,
  type DemoEvent,
  type DemoEventTone,
  type DemoLeadSnapshot,
} from './demoModel.js';
import { useDemoEngine, type DemoEngine } from './useDemoEngine.js';
import type { ClientMode, ClientWorkspace } from './previewModel.js';
import ClientAvailability from './ClientAvailability.js';
import {
  createDefaultAvailabilitySettings,
  evaluateAvailabilityDecision,
  type AvailabilitySettings,
} from './availabilityModel.js';
import './clientExperience.css';

export type { ClientMode } from './previewModel.js';
type ViewId = 'dashboard' | 'live' | 'journeys' | 'results' | 'availability';

const COLORS = {
  ink: '#0D1729',
  muted: '#64748B',
  blue: '#2E66E0',
  violet: '#6D4AEA',
  teal: '#0D9488',
  amber: '#D97706',
  green: '#159662',
  red: '#DC2626',
};

const NAV_ITEMS: { id: ViewId; label: string; icon: IconName; badge?: string }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
  { id: 'live', label: 'Live', icon: 'live', badge: '1' },
  { id: 'journeys', label: 'Lead journeys', icon: 'message', badge: '2' },
  { id: 'results', label: 'Results', icon: 'calendar' },
  { id: 'availability', label: 'Availability', icon: 'calendar' },
];

export type IconName =
  | 'dashboard'
  | 'live'
  | 'message'
  | 'journey'
  | 'calendar'
  | 'connections'
  | 'play'
  | 'pause'
  | 'restart'
  | 'search'
  | 'chevron'
  | 'phone'
  | 'send'
  | 'check'
  | 'shield'
  | 'clock';

export function Icon({ name, size = 16 }: { name: IconName; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  const paths: Record<IconName, React.ReactNode> = {
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
    live: <><circle cx="12" cy="12" r="3"/><path d="M6.3 6.3a8 8 0 0 0 0 11.4M17.7 6.3a8 8 0 0 1 0 11.4"/></>,
    message: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>,
    journey: <><circle cx="5" cy="5" r="2"/><circle cx="19" cy="19" r="2"/><path d="M7 5h5a4 4 0 0 1 4 4v6M12 19H7a4 4 0 0 1-4-4v-2"/></>,
    calendar: <><rect x="3" y="4" width="18" height="17" rx="2"/><path d="M8 2v4M16 2v4M3 10h18"/></>,
    connections: <><path d="M8 12h8M12 8v8"/><circle cx="12" cy="12" r="3"/><path d="M3 12a9 9 0 0 1 9-9M21 12a9 9 0 0 1-9 9"/></>,
    play: <polygon points="7 4 19 12 7 20 7 4" fill="currentColor" stroke="none"/>,
    pause: <><rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" stroke="none"/><rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" stroke="none"/></>,
    restart: <><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v6h6"/></>,
    search: <><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></>,
    chevron: <path d="m9 18 6-6-6-6"/>,
    phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.4 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z"/>,
    send: <><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="m9 12 2 2 4-4"/></>,
    clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  };

  return <svg {...common}>{paths[name]}</svg>;
}

export function AclAutoLogo({ compact = false }: { compact?: boolean }) {
  return (
    <div className="acl-logo" aria-label="ACL Auto by AtCostLeads">
      <img src={aclLogoBlue} alt="AtCostLeads.com"/>
      <span className="acl-product-label">AUTO</span>
      {compact && <span className="sr-only">ACL Auto</span>}
    </div>
  );
}

function toneColor(tone: DemoEventTone): string {
  if (tone === 'success') return COLORS.green;
  if (tone === 'warning') return COLORS.amber;
  if (tone === 'info') return COLORS.blue;
  return COLORS.muted;
}

function channelLabel(channel: DemoChannel | null): string {
  const labels: Record<DemoChannel, string> = {
    meta: 'Lead form',
    validation: 'Phone check',
    voice: 'Voice',
    imessage: 'iMessage',
    sms: 'SMS',
    calendar: 'Calendar',
    system: 'ACL Auto',
  };
  return channel ? labels[channel] : 'Waiting';
}

function clientTitle(event: DemoEvent): string {
  return event.title
    .replace('New Meta lead', 'New lead received')
    .replace('10DLC SMS fallback ready', 'SMS fallback ready')
    .replace('Google Calendar', 'calendar');
}

function clientDetail(event: DemoEvent): string {
  return event.detail
    .replace(/IPQS/gi, 'phone intelligence')
    .replace(/Sendblue/gi, 'secure messaging')
    .replace(/SignalHouse/gi, 'carrier messaging')
    .replace(/Retell/gi, 'voice agent')
    .replace(/ElevenLabs/gi, 'voice agent')
    .replace(/10DLC/gi, 'carrier')
    .replace(/Google Calendar/gi, 'the connected calendar')
    .replace(/Meta lead form/gi, 'lead form');
}

function outcomeLabel(lead: DemoLeadSnapshot): string {
  return (lead.outcome ?? lead.statusLabel)
    .replace('Google Calendar booking', 'Appointment booked')
    .replace('Opted out', 'Suppressed safely');
}

function currentEvent(engine: DemoEngine): DemoEvent | undefined {
  return engine.events[engine.events.length - 1];
}

const DEMO_NOW = new Date();

function dateLabel(offset = 0): string {
  const date = new Date(DEMO_NOW);
  date.setDate(date.getDate() + offset);
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(date);
}

function dateAtOffset(offsetMinutes: number): Date {
  return new Date(DEMO_NOW.getTime() + offsetMinutes * 60_000);
}

function clockLabel(date: Date): string {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
}

function fullDateLabel(date: Date): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(date);
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function relativeDayLabel(date: Date): string {
  const tomorrow = new Date(DEMO_NOW);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateKey(date) === dateKey(DEMO_NOW)) return 'Today';
  if (dateKey(date) === dateKey(tomorrow)) return 'Tomorrow';
  return new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
}

function Sidebar({ view, setView, engine, mode, workspace }: { view: ViewId; setView: (view: ViewId) => void; engine: DemoEngine; mode: ClientMode; workspace: ClientWorkspace }) {
  return (
    <aside className="acl-sidebar" aria-label="ACL Auto primary navigation">
      <div className="acl-sidebar-logo"><AclAutoLogo/></div>
      <p className="acl-nav-eyebrow">{mode === 'demo' ? 'Demo client' : 'Client workspace'}</p>
      <nav className="acl-nav">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={view === item.id ? 'acl-nav-item active' : 'acl-nav-item'}
            onClick={() => setView(item.id)}
            aria-current={view === item.id ? 'page' : undefined}
          >
            <Icon name={item.icon}/><span>{item.label}</span>
            {item.badge && !(workspace.paused && item.id === 'live') && <b>{item.badge}</b>}
          </button>
        ))}
      </nav>
      <div className="acl-sidebar-footer">
        <div className="acl-ava-status">
          <AiOrb size={28}/>
          <span><strong>{workspace.aiName}</strong><small>{workspace.paused ? 'workspace paused' : engine.isPlaying ? 'working now' : 'standing by'} · {engine.clockLabel}</small></span>
        </div>
        <div className="acl-client">
          <span className="acl-client-avatar">{workspace.name.charAt(0)}</span>
          <span><strong>{workspace.name}</strong><small>{workspace.company ? `${workspace.company} · ` : ''}{workspace.paused ? 'Paused' : mode === 'demo' ? 'Demo' : 'Live'}</small></span>
        </div>
      </div>
    </aside>
  );
}

function BottomNav({ view, setView, paused }: { view: ViewId; setView: (view: ViewId) => void; paused: boolean }) {
  return (
    <nav className="acl-bottom-nav" aria-label="ACL Auto mobile navigation">
      {NAV_ITEMS.map((item) => (
        <button
          key={item.id}
          type="button"
          className={view === item.id ? 'active' : ''}
          onClick={() => setView(item.id)}
          aria-current={view === item.id ? 'page' : undefined}
          aria-label={item.label}
        >
          <span className="acl-bottom-icon"><Icon name={item.icon}/>{item.badge && !(paused && item.id === 'live') && <b>{item.badge}</b>}</span>
          <small>{item.id === 'journeys' ? 'Journeys' : item.label}</small>
        </button>
      ))}
    </nav>
  );
}

function SampleNotice() {
  return (
    <div className="sample-notice" role="note">
      <span className="sample-dot"/>
      <strong>Demo workspace</strong>
      <span>Synthetic leads · No outreach sent</span>
    </div>
  );
}

function DemoController({ engine }: { engine: DemoEngine }) {
  return (
    <section className="demo-controller demo-outcome-picker" aria-label="Interactive demo outcome picker">
      <div className="outcome-picker-heading">
        <div className="outcome-picker-copy">
          <small>INTERACTIVE DEMO</small>
          <strong>Choose an outcome to watch</strong>
          <p>Selecting an outcome replays a sample lead path from arrival to result.</p>
        </div>
        <div className="playback-controls">
          <button type="button" className="replay-control" onClick={engine.restart} aria-label="Replay selected sample path">
            <Icon name="restart" size={14}/> Replay
          </button>
          <button type="button" className="play-control" onClick={engine.togglePlayback} aria-label={engine.isPlaying ? 'Pause selected sample path' : 'Play selected sample path'}>
            <Icon name={engine.isPlaying ? 'pause' : 'play'} size={13}/>
            {engine.isPlaying ? 'Pause' : engine.isComplete ? 'Play again' : 'Play'}
          </button>
        </div>
      </div>
      <div className="scenario-tabs" role="group" aria-label="Choose a demo outcome">
        {engine.scenarios.map((scenario) => (
          <button
            key={scenario.id}
            type="button"
            aria-pressed={engine.selectedScenarioId === scenario.id}
            className={engine.selectedScenarioId === scenario.id ? 'active' : ''}
            onClick={() => engine.selectScenario(scenario.id)}
          >
            {scenario.shortTitle}
          </button>
        ))}
      </div>
      <div className="demo-scenario-description" aria-live="polite">
        <span><strong>{engine.scenario.shortTitle}</strong><small>{engine.scenario.description}</small></span>
        <em>{engine.isComplete ? 'Sample complete' : engine.isPlaying ? 'Replaying sample' : 'Sample paused'}</em>
      </div>
      <div className="demo-progress" role="progressbar" aria-label={`${engine.scenario.shortTitle} sample progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(engine.progress * 100)}><span style={{ width: `${engine.progress * 100}%` }}/></div>
    </section>
  );
}

function PageHeader({ title, subtitle, action }: { title: string; subtitle: string; action?: React.ReactNode }) {
  return (
    <header className="page-header">
      <div><h1>{title}</h1><p>{subtitle}</p></div>
      {action}
    </header>
  );
}

function LiveOpsStrip({ engine, onOpen, workspace }: { engine: DemoEngine; onOpen: () => void; workspace: ClientWorkspace }) {
  const latest = currentEvent(engine);
  return (
    <section className={workspace.paused ? 'live-ops-strip workspace-paused' : 'live-ops-strip'} aria-label={`${workspace.aiName} working status`}>
      <div className="live-ops-title">
        <AiOrb size={34}/>
        <span><strong>{workspace.paused ? `${workspace.aiName} is paused` : `${workspace.aiName} is working your leads`}</strong><small>{workspace.paused ? '0 active · outreach and intake held' : `${engine.isPlaying ? '1 active' : 'standing by'} · ${channelLabel(engine.lead.activeChannel)} · ${engine.lead.statusLabel.toLowerCase()}`}</small></span>
        <Waveform color="rgba(255,255,255,.55)" height={18}/>
      </div>
      <div className="live-lead-mini">
        <AvatarInitials name={engine.lead.name} size={26} ring={workspace.paused ? 'none' : 'green'} badge={!workspace.paused}/>
        <span><strong>{engine.lead.name}</strong><small>{workspace.paused ? 'No active outreach' : latest ? clientTitle(latest) : 'New lead queued'}</small></span>
        <button type="button" onClick={onOpen}>Open activity</button>
      </div>
      <span className="live-route-chip">{workspace.paused ? 'Workspace paused' : channelLabel(engine.lead.activeChannel)}</span>
    </section>
  );
}

function EventDot({ event, latest = false }: { event: DemoEvent; latest?: boolean }) {
  const color = toneColor(event.tone);
  return <span className={latest ? 'event-dot latest' : 'event-dot'} style={{ color, background: `${color}16`, borderColor: `${color}42` }}><span style={{ background: color }}/></span>;
}

function ActiveLeadTheater({ engine, openJourney, mode, aiName }: { engine: DemoEngine; openJourney: () => void; mode: ClientMode; aiName: string }) {
  const latest = currentEvent(engine);
  const visible = engine.events.slice(-5);
  return (
    <section className="card active-lead-card">
      <div className="card-heading active-lead-heading">
        <div><span className="eyebrow"><span className="live-pip"/> Lead in motion</span><h2>{engine.lead.name}</h2><p>{engine.lead.phone} · {engine.lead.location} · {engine.lead.createdAtLabel}</p></div>
        <span className="status-chip" data-tone={engine.lead.suppressed ? 'warning' : engine.lead.qualified || engine.lead.outcome ? 'success' : 'info'}>{engine.lead.statusLabel}</span>
      </div>
      <div className="theater-stage">
        <div className="theater-avatar"><AvatarInitials name={engine.lead.name} size={46} ring={engine.lead.stage === 'talking' ? 'green' : 'none'} badge/><span><strong>{latest ? clientTitle(latest) : 'Lead received'}</strong><small>{latest ? clientDetail(latest) : `${aiName} is preparing the first touch.`}</small></span></div>
        {latest?.transcript && (
          <blockquote className={latest.transcript.speaker === 'lead' ? 'lead-quote inbound' : 'lead-quote'}>
            <span>{latest.transcript.speaker === 'lead' ? engine.lead.name : aiName}</span>
            “{latest.transcript.text}”
          </blockquote>
        )}
        {!latest?.transcript && <div className="signal-sweep"><Waveform color={COLORS.violet} barCount={12} height={28}/><span>{engine.lead.statusDetail}</span></div>}
      </div>
      <div className="mini-timeline">
        {visible.map((event, index) => (
          <div key={event.id} className="mini-event">
            <EventDot event={event} latest={index === visible.length - 1}/>
            <span><strong>{clientTitle(event)}</strong><small>{channelLabel(event.channel)} · {Math.floor(event.atMs / 1000)}s</small></span>
          </div>
        ))}
        {visible.length === 0 && <p className="empty-copy">The first event will appear as the lead journey begins.</p>}
      </div>
      <div className="card-footer-row">
        <span><Icon name="shield" size={14}/> {mode === 'demo' ? 'Sample event history' : 'Event history'} · read only</span>
        <button type="button" className="text-link" onClick={openJourney}>View full journey <Icon name="chevron" size={12}/></button>
      </div>
    </section>
  );
}

function AppointmentsCard({ onOpen, mode, owner }: { onOpen: () => void; mode: ClientMode; owner: string }) {
  const appointments = [
    { at: dateAtOffset(45), name: 'Jordan Ellis', detail: `Coverage review · ${owner}` },
    { at: dateAtOffset(165), name: 'Marcus Bell', detail: `Agent follow-up · ${owner}` },
  ];
  return (
    <section className="card appointments-card">
      <div className="card-heading"><div><h2>Upcoming appointments</h2><p>{mode === 'demo' ? 'Next on your sample calendar' : 'Next on your connected calendar'}</p></div><button type="button" className="text-link" onClick={onOpen}>Full results <Icon name="chevron" size={12}/></button></div>
      {appointments.map((appointment) => (
        <div className="appointment-row" key={appointment.name}>
          <time>{clockLabel(appointment.at)}</time>
          <span><strong>{appointment.name}</strong><small>{appointment.detail}</small></span>
          <b>{relativeDayLabel(appointment.at).toLowerCase()}</b>
        </div>
      ))}
    </section>
  );
}

function NeedsAttention({ aiName }: { aiName: string }) {
  const [sent, setSent] = useState(false);
  return (
    <section className="card attention-card">
      <div className="attention-title"><span>⚡ NEEDS YOUR ATTENTION</span><b>1</b></div>
      <div className="attention-body"><p>After your team’s transfer window closes, {aiName} will book qualified leads into the next open times.</p><button type="button" onClick={() => setSent(true)}>{sent ? 'Request sent' : 'Request change'}</button></div>
    </section>
  );
}

function PausedWorkspaceCard({ workspace }: { workspace: ClientWorkspace }) {
  return <section className="card paused-workspace-card"><span><Icon name="pause" size={17}/></span><div><h2>Outreach is paused</h2><p>ACL Operations has held new intake, calls, and messages for {workspace.name}. Historical results remain available while the managed routing issue is reviewed.</p><small>0 active sessions · No synthetic playback running</small></div></section>;
}

function DashboardSummary({ settings, workspace, onOpenAvailability }: { settings: AvailabilitySettings; workspace: ClientWorkspace; onOpenAvailability: () => void }) {
  const decision = evaluateAvailabilityDecision(settings, workspace.paused);
  const metrics = [
    { label: 'New leads', value: '24', detail: 'today', tone: 'blue' },
    { label: 'Contacted', value: '18', detail: '75% contact rate', tone: 'teal' },
    { label: 'Conversations', value: '11', detail: '46% of new leads', tone: 'cyan' },
    { label: 'Qualified', value: '7', detail: '64% of conversations', tone: 'violet' },
    { label: 'Appointments', value: '4', detail: 'booked today', tone: 'amber' },
    { label: 'Live transfers', value: '3', detail: 'connected today', tone: 'green' },
  ];
  return (
    <section className="client-dashboard-summary">
      <div className="client-summary-heading">
        <div><small>TODAY'S PIPELINE</small><h2>From new lead to agent-ready</h2></div>
        <button type="button" onClick={onOpenAvailability}>Manage availability <Icon name="chevron" size={13}/></button>
      </div>
      <div className="client-summary-metrics">
        {metrics.map((metric) => <div key={metric.label} data-tone={metric.tone}><span>{metric.label}</span><strong>{metric.value}</strong><small>{metric.detail}</small></div>)}
      </div>
      <button type="button" className={`client-summary-decision ${decision.kind}`} onClick={onOpenAvailability}>
        <span className="summary-decision-icon"><Icon name={decision.kind === 'live-transfer' ? 'phone' : decision.kind === 'booking' ? 'calendar' : 'pause'} size={17}/></span>
        <span><small>NEXT QUALIFIED LEAD · TUESDAY 11:15 AM</small><strong>{decision.label}</strong><em>{decision.detail}</em></span>
        <Icon name="chevron" size={15}/>
      </button>
    </section>
  );
}

function RecentLeadsCard({ engine, onOpen }: { engine: DemoEngine; onOpen: (leadId: string) => void }) {
  const details: Record<string, { preview: string; time: string }> = {
    'Maya Carter': { preview: 'Requested mortgage protection details', time: 'now' },
    'Jordan Ellis': { preview: 'Tomorrow at 2 works for me.', time: '4m' },
    'Taylor Nguyen': { preview: 'Qualified callback requested', time: '9m' },
    'Casey Monroe': { preview: 'STOP received · suppressed safely', time: '14m' },
  };
  return (
    <section className="card client-recent-leads">
      <div className="card-heading"><div><h2>Recent leads</h2><p>Latest conversations and outcomes</p></div><button type="button" className="text-link" onClick={() => onOpen(engine.selectedLeadId)}>All journeys <Icon name="chevron" size={12}/></button></div>
      <div className="recent-lead-header" aria-hidden="true"><span>Lead</span><span>Channel</span><span>Outcome</span><span>Time</span></div>
      <div className="recent-lead-list">
        {engine.leads.slice(0, 4).map((lead) => {
          const detail = details[lead.name] ?? { preview: lead.statusDetail, time: 'now' };
          return <button type="button" className={lead.id === engine.selectedLeadId ? 'active' : ''} key={lead.id} onClick={() => onOpen(lead.id)}><AvatarInitials name={lead.name} size={36}/><span className="recent-lead-person"><strong>{lead.name}</strong><small>{detail.preview}</small></span><span className="recent-lead-channel">{channelLabel(lead.preferredChannel)}</span><span className="recent-lead-outcome">{outcomeLabel(lead)}</span><time>{detail.time}</time><Icon name="chevron" size={13}/></button>;
        })}
      </div>
    </section>
  );
}

function Dashboard({ engine, setView, mode, workspace, availabilitySettings }: { engine: DemoEngine; setView: (view: ViewId) => void; mode: ClientMode; workspace: ClientWorkspace; availabilitySettings: AvailabilitySettings }) {
  const openLead = (leadId: string) => {
    engine.viewLead(leadId);
    setView('journeys');
  };
  return (
    <div className="standard-screen">
      <PageHeader title="Dashboard" subtitle={workspace.paused ? `${workspace.name} is paused. Historical performance remains visible.` : `A live view of the work ACL Auto is doing for ${workspace.name}`} action={<div className="date-pill">Today · {dateLabel(0)}</div>}/>
      {mode === 'demo' && !workspace.paused && <DemoController engine={engine}/>}
      <LiveOpsStrip engine={engine} workspace={workspace} onOpen={() => setView('live')}/>
      <DashboardSummary settings={availabilitySettings} workspace={workspace} onOpenAvailability={() => setView('availability')}/>
      <div className="dashboard-theater-grid">
        {workspace.paused ? <PausedWorkspaceCard workspace={workspace}/> : <ActiveLeadTheater engine={engine} mode={mode} aiName={workspace.aiName} openJourney={() => setView('journeys')}/>}
        <div className="dashboard-side-stack"><AppointmentsCard mode={mode} owner={workspace.name} onOpen={() => setView('results')}/><NeedsAttention aiName={workspace.aiName}/></div>
      </div>
      <RecentLeadsCard engine={engine} onOpen={openLead}/>
    </div>
  );
}

function LiveCallCard({ engine, setView, aiName }: { engine: DemoEngine; setView: (view: ViewId) => void; aiName: string }) {
  const latest = currentEvent(engine);
  const transcript = [...engine.events].reverse().find((event) => event.transcript)?.transcript;
  const isCall = engine.lead.activeChannel === 'voice' || engine.lead.stage === 'talking' || engine.lead.stage === 'dialing';
  const sentiment = engine.lead.suppressed
    ? { label: 'Opt-out detected', tone: 'warning' }
    : engine.lead.qualified
      ? { label: 'Positive', tone: 'positive' }
      : { label: 'Neutral', tone: 'neutral' };
  return (
    <section className={engine.transfer.state === 'ready' || engine.transfer.state === 'connecting' ? 'card live-call-card transfer-ready' : 'card live-call-card'}>
      <div className="live-call-head"><AvatarInitials name={engine.lead.name} size={42} ring={isCall ? 'green' : 'none'} badge/><span><h2>{engine.lead.name}</h2><p>{engine.lead.location} · {channelLabel(engine.lead.activeChannel)}</p></span><time>{engine.clockLabel.split(' / ')[0]}</time></div>
      <div className="live-transcript">
        {transcript ? <p><strong>{transcript.speaker === 'lead' ? engine.lead.name : aiName}</strong> “{transcript.text}”</p> : <p><strong>{aiName}</strong> {latest ? clientDetail(latest) : 'Preparing the first contact attempt.'}</p>}
      </div>
      <div className="live-wave"><Waveform color={isCall ? COLORS.green : COLORS.violet} barCount={10} height={17}/><span>{engine.isPlaying ? 'Session in progress' : engine.isComplete ? 'Session completed' : 'Session paused'}</span></div>
      <div className="live-call-actions"><span className="status-chip" data-tone={engine.lead.suppressed ? 'warning' : engine.lead.qualified ? 'success' : 'info'}>{engine.lead.statusLabel}</span><span className="sentiment-chip" data-tone={sentiment.tone}>{sentiment.label}</span><button type="button" onClick={() => setView('journeys')}>Open journey</button></div>
    </section>
  );
}

function LiveView({ engine, setView, mode, workspace }: { engine: DemoEngine; setView: (view: ViewId) => void; mode: ClientMode; workspace: ClientWorkspace }) {
  const queue = [
    { id: 'queue-morgan', name: 'Morgan Vale', eta: '+2m', reason: 'Fresh lead sequence' },
    { id: 'queue-theo', name: 'Theo North', eta: '+6m', reason: 'Opposite-daypart retry' },
    { id: 'queue-jamie', name: 'Jamie Park', eta: '+11m', reason: 'Voice follow-up window' },
  ];
  return (
    <div className="standard-screen">
      <PageHeader title="Live activity" subtitle={workspace.paused ? `${workspace.name} has no active outreach sessions` : `Watch ${workspace.aiName} work calls, replies, and handoffs as they happen`} action={<span className={workspace.paused ? 'paused-page-badge' : 'live-page-badge'}>{!workspace.paused && <span/>} {workspace.paused ? 'PAUSED' : mode === 'demo' ? 'LIVE SAMPLE' : 'ACTIVE'}</span>}/>
      {mode === 'demo' && !workspace.paused && <DemoController engine={engine}/>}
      {workspace.paused && <PausedWorkspaceCard workspace={workspace}/>}
      <div className="live-grid">
        <div className="live-primary">{!workspace.paused && <LiveCallCard engine={engine} aiName={workspace.aiName} setView={setView}/>}<section className="card activity-ledger"><div className="card-heading"><div><h2>{workspace.paused ? 'Recent activity' : 'Activity log'}</h2><p>{workspace.paused ? 'No new events while this workspace is paused' : mode === 'demo' ? 'Sample events from this lead' : 'Events from this lead'}</p></div></div>{engine.activity.slice(0, 9).map((item) => <div className="ledger-row" key={item.id}><EventDot event={item} latest={item.isLatest}/><span><strong>{clientTitle(item)}</strong><small>{clientDetail(item)}</small></span><time>{item.elapsedLabel}</time></div>)}{workspace.paused && <p className="operator-empty">No activity generated while paused.</p>}</section></div>
        <aside className="live-side"><section className="card queue-card"><div className="card-heading"><div><h2>{workspace.paused ? 'Queue held' : 'Up next'}</h2><p>{workspace.paused ? 'No attempts will start until ACL Operations resumes this workspace' : `${queue.length} separate ${mode === 'demo' ? 'sample ' : ''}leads queued`}</p></div></div>{!workspace.paused && queue.map((lead) => <div className="queue-row" key={lead.id}><time>{lead.eta}</time><span><strong>{lead.name}</strong><small>{lead.reason}</small></span></div>)}</section><section className="card guardrail-card"><div className="guardrail-icon"><Icon name="shield"/></div><div><h2>Guardrails active</h2><p>Local-time windows, phone checks, and opt-out suppression are applied before each touch.</p></div></section></aside>
      </div>
    </div>
  );
}

type ConversationTab = 'unread' | 'all' | 'needs' | 'booked';

function ConversationList({ engine, search, setSearch, select, onOpenLive }: { engine: DemoEngine; search: string; setSearch: (value: string) => void; select: (id: string) => void; onOpenLive: () => void }) {
  const [tab, setTab] = useState<ConversationTab>('all');
  const matchesSearch = engine.leads.filter((lead) => lead.name.toLowerCase().includes(search.toLowerCase()) || lead.phone.includes(search));
  const filtered = matchesSearch.filter((lead, index) => {
    if (tab === 'unread') return index < 2;
    if (tab === 'needs') return lead.suppressed;
    if (tab === 'booked') return outcomeLabel(lead).toLowerCase().includes('book');
    return true;
  });
  const tabs: { id: ConversationTab; label: string; count?: number; tone?: string }[] = [
    { id: 'unread', label: 'Unread', count: 2 },
    { id: 'all', label: 'All' },
    { id: 'needs', label: 'Needs you', count: 1, tone: 'amber' },
    { id: 'booked', label: 'Booked' },
  ];
  return (
    <div className="conversation-list">
      <div className="conversation-search"><Icon name="search" size={14}/><input aria-label="Search conversations" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search conversations..."/></div>
      <button type="button" className="conversation-live-note" onClick={onOpenLive}>1 call · 1 text live now →</button>
      <div className="conversation-tabs" role="tablist" aria-label="Lead journey filters">
        {tabs.map((item) => <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} className={tab === item.id ? 'active' : ''} onClick={() => setTab(item.id)}>{item.label}{item.count && <b className={item.tone}>{item.count}</b>}</button>)}
      </div>
      <div className="conversation-rows">
        {filtered.map((lead, index) => <button key={lead.id} type="button" className={lead.id === engine.selectedLeadId ? 'selected' : ''} onClick={() => select(lead.id)}><AvatarInitials name={lead.name} size={34}/><span><strong>{lead.name}</strong><small><i style={{ background: lead.suppressed ? '#64748B' : lead.outcome ? COLORS.green : COLORS.teal }}/>{outcomeLabel(lead)}</small></span><time>{index === 0 ? 'now' : `${index + 1}m`}</time></button>)}
      </div>
    </div>
  );
}

function ConversationThread({ engine, mode, aiName, mobileBack }: { engine: DemoEngine; mode: ClientMode; aiName: string; mobileBack?: () => void }) {
  const events = engine.events.filter((event) => event.transcript || ['lead_received', 'phone_validated', 'calendar_booked', 'transfer_connected', 'suppression_applied', 'handoff_completed'].includes(event.kind));
  return (
    <div className="conversation-thread">
      <div className="thread-header">{mobileBack && <button type="button" onClick={mobileBack} className="thread-back">← Back</button>}<AvatarInitials name={engine.lead.name} size={34}/><span><h1>{engine.lead.name}</h1><p>{engine.lead.phone} · {engine.lead.location}</p></span><span className="status-chip" data-tone={engine.lead.suppressed ? 'warning' : 'info'}>{engine.lead.statusLabel}</span></div>
      <div className="thread-scroll" tabIndex={0} aria-label={`${engine.lead.name} conversation history`}>
        <div className="thread-day">TODAY{mode === 'demo' ? ' · SAMPLE' : ''}</div>
        {events.map((event) => event.transcript ? <div key={event.id} className={event.transcript.speaker === 'lead' ? 'message-bubble inbound' : 'message-bubble outbound'}><div>{event.transcript.text}</div><small>{event.transcript.speaker === 'lead' ? engine.lead.name : `✦ ${aiName}`} · {channelLabel(event.channel)} · {Math.floor(event.atMs / 1000)}s</small></div> : <div key={event.id} className="thread-system"><EventDot event={event}/><span><strong>{clientTitle(event)}</strong><small>{clientDetail(event)} · {Math.floor(event.atMs / 1000)}s</small></span></div>)}
        {events.length === 0 && <p className="thread-empty">{aiName} is preparing this conversation.</p>}
      </div>
      <div className="read-only-composer"><Icon name="shield" size={15}/><span><strong>Read-only client view</strong><small>ACL Auto is handling this conversation</small></span><button type="button" disabled aria-label="Sending is unavailable in client view"><Icon name="send" size={15}/></button></div>
    </div>
  );
}

function ContactRail({ engine }: { engine: DemoEngine }) {
  return (
    <aside className="contact-rail">
      <div><small>LEAD STATUS</small><strong>{outcomeLabel(engine.lead)}</strong></div>
      <section><h2>Contact</h2><dl><dt>Source</dt><dd>Lead form</dd><dt>Phone</dt><dd>{engine.lead.phone}</dd><dt>Email</dt><dd>{engine.lead.email}</dd><dt>Location</dt><dd>{engine.lead.location}</dd></dl></section>
      <section><h2>Phone intelligence</h2><span className="route-chip">{engine.lead.phoneIntel.lineType}</span><dl><dt>Text capable</dt><dd>{engine.lead.phoneIntel.canText ? 'Yes' : 'No'}</dd><dt>Risk</dt><dd>Low</dd><dt>Preferred</dt><dd>{channelLabel(engine.lead.preferredChannel)}</dd></dl></section>
      <section><h2>Qualification</h2>{['Request confirmed', 'State matched', engine.lead.phoneIntel.lineType === 'landline' ? 'Voice route selected' : 'Contact route selected'].map((item) => <p className="qual-item" key={item}><span><Icon name="check" size={9}/></span>{item}</p>)}</section>
    </aside>
  );
}

function Journeys({ engine, setView, mode, workspace }: { engine: DemoEngine; setView: (view: ViewId) => void; mode: ClientMode; workspace: ClientWorkspace }) {
  const [search, setSearch] = useState('');
  const [mobileThread, setMobileThread] = useState(false);
  const select = (id: string) => {
    if (id !== engine.selectedLeadId) engine.viewLead(id);
    setMobileThread(true);
  };

  return (
    <div className="standard-screen journey-screen">
      <PageHeader title="Lead journeys" subtitle="Read-only conversations, contact decisions, and outcomes in one record"/>
      {mode === 'demo' && !workspace.paused && <DemoController engine={engine}/>}
      <div className={mobileThread ? 'journey-inbox-shell mobile-thread-open' : 'journey-inbox-shell'}>
        <ConversationList engine={engine} search={search} setSearch={setSearch} select={select} onOpenLive={() => setView('live')}/>
        <ConversationThread engine={engine} mode={mode} aiName={workspace.aiName} mobileBack={() => setMobileThread(false)}/>
        <ContactRail engine={engine}/>
      </div>
    </div>
  );
}

function Results({ mode, workspace }: { mode: ClientMode; workspace: ClientWorkspace }) {
  const appointments = [
    { at: dateAtOffset(45), name: 'Theo North', type: 'Coverage consultation', owner: workspace.name, status: `Booked by ${workspace.aiName}` },
    { at: dateAtOffset(165), name: 'Jordan Ellis', type: 'Coverage review', owner: workspace.name, status: 'Confirmed' },
    { at: dateAtOffset(300), name: 'Marcus Bell', type: 'Agent follow-up', owner: workspace.name, status: 'Confirmed' },
    { at: dateAtOffset(1_440), name: 'Taylor Nguyen', type: 'Qualified callback', owner: workspace.name, status: 'Callback' },
  ];
  const groups = Array.from(appointments.reduce((result, appointment) => {
    const key = dateKey(appointment.at);
    const group = result.get(key) ?? { date: appointment.at, appointments: [] as typeof appointments };
    group.appointments.push(appointment);
    result.set(key, group);
    return result;
  }, new Map<string, { date: Date; appointments: typeof appointments }>()), ([, group]) => group);
  return (
    <div className="standard-screen">
      <PageHeader title="Results" subtitle="The conversations ACL Auto turned into agent opportunities" action={<div className="date-pill">Last 7 days</div>}/>
      <div className="results-kpis"><KpiCard label="Leads worked" value="136" borderColor={COLORS.blue}/><KpiCard label="Contacted" value="92" borderColor={COLORS.teal}/><KpiCard label="Qualified" value="31" borderColor={COLORS.violet}/><KpiCard label="Appointments" value="18" borderColor={COLORS.amber}/><KpiCard label="Live transfers" value="11" borderColor={COLORS.green}/></div>
      <div className="results-grid"><section className="card results-calendar"><div className="card-heading"><div><h2>Upcoming</h2><p>{mode === 'demo' ? 'Sample schedule from your connected calendar' : 'Schedule from your connected calendar'}</p></div><span className="route-chip">NEXT OPEN TIMES</span></div>{groups.map((group) => <React.Fragment key={dateKey(group.date)}><div className="tomorrow-divider"><span>{relativeDayLabel(group.date)} · {fullDateLabel(group.date)}</span></div>{group.appointments.map((appointment) => <div className="result-appointment" key={`${appointment.at.toISOString()}-${appointment.name}`}><time>{clockLabel(appointment.at)}</time><AvatarInitials name={appointment.name} size={34}/><span><strong>{appointment.name}</strong><small>{appointment.type} · {appointment.owner}</small></span><b>{appointment.status}</b></div>)}</React.Fragment>)}</section><aside className="results-side"><section className="card result-breakdown"><div className="card-heading"><div><h2>Outcome mix</h2><p>Last 7 days</p></div></div>{[['Live transfers', 11, COLORS.violet], ['Appointments', 18, COLORS.amber], ['Qualified callbacks', 9, COLORS.teal], ['Still working', 44, COLORS.blue]].map(([label, value, color]) => <div key={String(label)}><span><i style={{ background: String(color) }}/>{label}</span><strong>{value}</strong></div>)}</section><section className="card result-note"><span className="violet-star">✦</span><div><h2>{workspace.aiName}’s impact</h2><p>92 leads contacted and 38 agent-ready opportunities created from 136 leads worked.</p></div></section></aside></div>
    </div>
  );
}

export default function AclAutoDemo({ mode = 'demo', workspace }: { mode?: ClientMode; workspace: ClientWorkspace }) {
  const engine = useDemoEngine({ autoPlay: !workspace.paused, initialSpeed: 1 });
  const [view, setView] = useState<ViewId>('dashboard');
  const [availabilitySettings, setAvailabilitySettings] = useState<AvailabilitySettings>(() => createDefaultAvailabilitySettings(workspace.timezone, workspace.phone));
  const activeLabel = useMemo(() => NAV_ITEMS.find((item) => item.id === view)?.label ?? 'Dashboard', [view]);
  const navigate = (nextView: ViewId) => {
    setView(nextView);
    window.scrollTo({ top: 0, left: 0 });
  };

  const screen = {
    dashboard: <Dashboard engine={engine} setView={navigate} mode={mode} workspace={workspace} availabilitySettings={availabilitySettings}/>,
    live: <LiveView engine={engine} setView={navigate} mode={mode} workspace={workspace}/>,
    journeys: <Journeys engine={engine} setView={navigate} mode={mode} workspace={workspace}/>,
    results: <Results mode={mode} workspace={workspace}/>,
    availability: <ClientAvailability settings={availabilitySettings} onSave={setAvailabilitySettings} mode={mode} workspace={workspace}/>,
  }[view];

  return (
    <div className={`acl-app-shell client-${mode}-shell${workspace.paused ? ' client-paused-shell' : ''}`}>
      <Sidebar view={view} setView={navigate} engine={engine} mode={mode} workspace={workspace}/>
      <main className="acl-main">
        {mode === 'demo' && <div className="sample-desktop"><SampleNotice/></div>}
        <div className="mobile-topbar"><AclAutoLogo/><span>{activeLabel}</span><span className={workspace.paused ? 'mobile-paused' : mode === 'demo' ? 'mobile-sample' : 'mobile-live'}>{workspace.paused ? 'PAUSED' : mode === 'demo' ? 'SAMPLE' : 'LIVE'}</span></div>
        {mode === 'demo' && <div className="sample-mobile"><SampleNotice/></div>}
        {screen}
      </main>
      <BottomNav view={view} setView={navigate} paused={workspace.paused}/>
    </div>
  );
}
